import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { COUNTRIES, MARITAL_STATUS_OPTIONS, getJurisdictionNuance } from '../../data/jurisdictionData';

const {
  FiX, FiArrowRight, FiArrowLeft, FiCheckCircle, FiUser, FiUsers,
  FiShield, FiAlertCircle, FiLock, FiCpu, FiPlus, FiTrash2,
  FiDownload, FiEdit3, FiHeart, FiFileText, FiInfo,
  FiGlobe, FiMapPin, FiChevronDown,
} = FiIcons;

// ── Reusable Field Components ──────────────────────────────────────────────
const Field = ({ label, placeholder, value, onChange, type = 'text', hint, required }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-accent-navy">
      {label}{required && <span className="text-accent-gold">*</span>}
    </label>
    {hint && <p className="text-[10px] text-text-muted font-light">{hint}</p>}
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-all placeholder:text-text-muted/50" />
  </div>
);

const SelectField = ({ label, value, onChange, options, hint, required, placeholder = '— Select —' }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-accent-navy">
      {label}{required && <span className="text-accent-gold">*</span>}
    </label>
    {hint && <p className="text-[10px] text-text-muted font-light">{hint}</p>}
    <div className="relative">
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-all appearance-none cursor-pointer pr-10">
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
      <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none" />
    </div>
  </div>
);

// ── Jurisdiction Nuance Banner ─────────────────────────────────────────────
const JurisdictionBanner = ({ nuance }) => {
  if (!nuance) return null;
  const rows = [
    { label: 'Legal System', value: nuance.legalSystem },
    { label: 'Witnesses Required', value: nuance.witnesses === 0 ? 'None (holographic)' : `${nuance.witnesses} independent witnesses` },
    { label: 'Notarisation', value: nuance.notarisation },
    { label: 'Holographic Will', value: nuance.holographic },
    { label: 'Probate Process', value: nuance.probate },
    { label: 'Minimum Age', value: `${nuance.minAge} years` },
    { label: 'Spouse / Partner Rights', value: nuance.spouseRights },
    { label: 'Executor Title', value: nuance.executorTerm },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border-2 border-accent-navy/20 bg-accent-navy/5 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 bg-accent-navy/10 border-b border-accent-navy/10">
        <div className="w-8 h-8 bg-accent-navy text-white rounded-xl flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiGlobe} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Jurisdiction Applied</p>
          <p className="text-sm font-bold text-accent-navy">{nuance.country}</p>
        </div>
      </div>
      <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {rows.map(r => (
          <div key={r.label} className="flex flex-col gap-0.5">
            <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">{r.label}</span>
            <span className="text-xs font-bold text-accent-navy">{r.value}</span>
          </div>
        ))}
      </div>
      {nuance.notes && (
        <div className="px-5 pb-5">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-100 rounded-xl">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-800 font-light leading-relaxed">{nuance.notes}</p>
          </div>
        </div>
      )}
    </motion.div>
  );
};

// ── Step 1: Personal Information + Jurisdiction ────────────────────────────
const StepPersonal = ({ data, onChange }) => {
  const selectedCountry = useMemo(() => COUNTRIES.find(c => c.value === data.country), [data.country]);
  const regionOptions = useMemo(() => selectedCountry ? selectedCountry.regions.map(r => ({ value: r, label: r })) : [], [selectedCountry]);
  const nuance = useMemo(() => data.country && data.region ? getJurisdictionNuance(data.country, data.region) : null, [data.country, data.region]);

  const handleCountryChange = (v) => {
    onChange('country', v);
    onChange('region', '');
  };

  return (
    <div className="space-y-6">
      <StepHeader icon={FiUser} title="Personal Information"
        subtitle="Tell us about yourself — the person making this will (the Testator)."
        step="Step 1 of 4" color="bg-blue-50 text-blue-500" />

      {/* ── Jurisdiction Selector ── */}
      <div className="p-6 bg-white border-2 border-brand-dark rounded-2xl space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent-gold/10 text-accent-gold rounded-xl flex items-center justify-center">
            <SafeIcon icon={FiGlobe} className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-bold text-accent-navy">Select Your Jurisdiction</p>
            <p className="text-[10px] text-text-muted font-light">Your will is governed by the laws of your country and region.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField label="Country" required placeholder="— Select Country —"
            value={data.country} onChange={handleCountryChange}
            options={COUNTRIES.map(c => ({ value: c.value, label: c.label }))} />
          <SelectField label={selectedCountry ? 'State / Province / Region' : 'Region'} required
            placeholder={data.country ? '— Select Region —' : '— Select Country First —'}
            value={data.region} onChange={v => onChange('region', v)}
            options={regionOptions} />
        </div>

        <AnimatePresence>
          {nuance && <JurisdictionBanner nuance={nuance} />}
        </AnimatePresence>
      </div>

      {/* ── Personal Details ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First Name" placeholder="e.g. John" required value={data.firstName} onChange={v => onChange('firstName', v)} />
        <Field label="Middle Name" placeholder="Optional" value={data.middleName} onChange={v => onChange('middleName', v)} />
        <Field label="Last Name" placeholder="e.g. Smith" required value={data.lastName} onChange={v => onChange('lastName', v)} />
        <Field label="Date of Birth" type="date" required value={data.dob} onChange={v => onChange('dob', v)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="Marital Status" required options={MARITAL_STATUS_OPTIONS}
          value={data.maritalStatus} onChange={v => onChange('maritalStatus', v)} />
        <Field label="Nationality / Citizenship" placeholder="e.g. British, American"
          value={data.nationality} onChange={v => onChange('nationality', v)}
          hint="Relevant for cross-border estates." />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Phone Number" placeholder="e.g. +1 555 123 4567" type="tel"
          value={data.phone} onChange={v => onChange('phone', v)} />
        <Field label="Email Address" placeholder="e.g. john@example.com" type="email"
          value={data.email} onChange={v => onChange('email', v)} />
      </div>

      <Field label="Home Address" placeholder="Street address, City, Postcode / ZIP"
        required value={data.address} onChange={v => onChange('address', v)} />

      {(data.maritalStatus === 'married' || data.maritalStatus === 'domestic_partnership') && (
        <div className="p-5 bg-blue-50 border border-blue-100 rounded-2xl space-y-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-blue-600 flex items-center gap-2">
            <SafeIcon icon={FiHeart} className="w-3.5 h-3.5" /> Spouse / Partner Details
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Spouse / Partner Full Name" placeholder="e.g. Jane Smith"
              value={data.spouseName} onChange={v => onChange('spouseName', v)} />
            <Field label="Spouse Date of Birth" type="date"
              value={data.spouseDob} onChange={v => onChange('spouseDob', v)} />
          </div>
        </div>
      )}

      <InfoBox text="All information is encrypted and stored securely. It will only be used to generate your will draft and is never shared with third parties." />
    </div>
  );
};

// ── Step 2: Executor Details ───────────────────────────────────────────────
const StepExecutor = ({ data, onChange, personal }) => {
  const nuance = useMemo(() => personal.country && personal.region
    ? getJurisdictionNuance(personal.country, personal.region) : null,
    [personal.country, personal.region]);

  const executorLabel = nuance?.executorTerm || 'Executor';

  const RELATIONSHIP = [
    { value: 'spouse', label: 'Spouse / Partner' },
    { value: 'child', label: 'Child' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'parent', label: 'Parent' },
    { value: 'friend', label: 'Friend' },
    { value: 'attorney', label: 'Attorney / Solicitor' },
    { value: 'trust_company', label: 'Trust Company / Bank' },
    { value: 'notary', label: 'Notary / Notario' },
    { value: 'other', label: 'Other' },
  ];

  const powers = [
    { key: 'sellProperty', label: 'Sell Real Property', desc: 'Authorise sale of real estate assets' },
    { key: 'manageInvestments', label: 'Manage Investments', desc: 'Handle stocks, bonds, and portfolios' },
    { key: 'payDebts', label: 'Pay Debts & Taxes', desc: 'Settle outstanding obligations' },
    { key: 'digitalAccess', label: 'Digital Asset Access', desc: 'Manage online accounts and digital property' },
  ];

  return (
    <div className="space-y-6">
      <StepHeader icon={FiShield} title={`${executorLabel} Details`}
        subtitle={`Your ${executorLabel.toLowerCase()} carries out the instructions in your will. Choose someone you trust completely.`}
        step="Step 2 of 4" color="bg-accent-gold/10 text-accent-gold" />

      {nuance && (
        <div className="flex items-start gap-3 p-4 bg-accent-navy/5 border border-accent-navy/10 rounded-xl">
          <SafeIcon icon={FiGlobe} className="w-4 h-4 text-accent-navy flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-accent-navy font-light leading-relaxed">
            <strong>In {nuance.country}</strong>, the executor role is called the <strong>{nuance.executorTerm}</strong>.
            {nuance.witnesses === 0
              ? ' No witnesses are required for a holographic will, but a notarial will may still require witnesses.'
              : ` Your will requires ${nuance.witnesses} independent witness${nuance.witnesses > 1 ? 'es' : ''}.`}
          </p>
        </div>
      )}

      {/* Primary */}
      <div className="p-6 bg-white border-2 border-brand-dark rounded-2xl space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-accent-navy text-white rounded-xl flex items-center justify-center text-xs font-black">1</div>
          <p className="font-serif text-lg text-accent-navy">Primary {executorLabel}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Legal Name" placeholder="e.g. Jane Smith" required
            value={data.primaryName} onChange={v => onChange('primaryName', v)} />
          <SelectField label="Relationship to You" required options={RELATIONSHIP}
            value={data.primaryRelationship} onChange={v => onChange('primaryRelationship', v)} />
          <Field label="Phone Number" placeholder="e.g. +44 7700 900123" type="tel"
            value={data.primaryPhone} onChange={v => onChange('primaryPhone', v)} />
          <Field label="Email Address" placeholder="e.g. jane@example.com" type="email"
            value={data.primaryEmail} onChange={v => onChange('primaryEmail', v)} />
        </div>
        <Field label="Home Address" placeholder="Street address, City, Postcode / ZIP"
          value={data.primaryAddress} onChange={v => onChange('primaryAddress', v)} />
      </div>

      {/* Alternate */}
      <div className="p-6 bg-brand-light border-2 border-dashed border-brand-dark rounded-2xl space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-dark text-text-muted rounded-xl flex items-center justify-center text-xs font-black">2</div>
          <div>
            <p className="font-serif text-lg text-accent-navy">Alternate {executorLabel}</p>
            <p className="text-[10px] text-text-muted font-light">Recommended — steps in if the primary is unable to serve.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Legal Name" placeholder="Optional"
            value={data.alternateName} onChange={v => onChange('alternateName', v)} />
          <SelectField label="Relationship to You" options={RELATIONSHIP}
            value={data.alternateRelationship} onChange={v => onChange('alternateRelationship', v)} />
          <Field label="Phone Number" placeholder="Optional" type="tel"
            value={data.alternatePhone} onChange={v => onChange('alternatePhone', v)} />
          <Field label="Email Address" placeholder="Optional" type="email"
            value={data.alternateEmail} onChange={v => onChange('alternateEmail', v)} />
        </div>
      </div>

      {/* Powers */}
      <div className="space-y-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Powers to Grant</p>
        <p className="text-xs text-text-muted font-light">Select the powers you wish to grant your {executorLabel.toLowerCase()}.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {powers.map(power => (
            <button key={power.key} onClick={() => onChange(power.key, !data[power.key])}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                data[power.key] ? 'bg-accent-navy/5 border-accent-navy' : 'bg-white border-brand-dark hover:border-accent-gold'
              }`}>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                data[power.key] ? 'bg-accent-navy border-accent-navy' : 'border-brand-dark'
              }`}>
                {data[power.key] && <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className="text-xs font-bold text-accent-navy">{power.label}</p>
                <p className="text-[10px] text-text-muted font-light">{power.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <InfoBox text={`Your ${executorLabel} has a fiduciary duty to act in the best interests of your estate and beneficiaries. Choose someone organised, trustworthy, and willing to take on this responsibility.`} />
    </div>
  );
};

// ── Step 3: Beneficiaries ─────────────────────────────────────────────────
const StepBeneficiaries = ({ data, onChange, personal }) => {
  const nuance = useMemo(() => personal.country && personal.region
    ? getJurisdictionNuance(personal.country, personal.region) : null,
    [personal.country, personal.region]);

  const RELATIONSHIP = [
    { value: 'spouse', label: 'Spouse / Partner' },
    { value: 'child', label: 'Child' },
    { value: 'grandchild', label: 'Grandchild' },
    { value: 'sibling', label: 'Sibling' },
    { value: 'parent', label: 'Parent' },
    { value: 'friend', label: 'Friend' },
    { value: 'charity', label: 'Charity / Non-Profit' },
    { value: 'trust', label: 'Trust' },
    { value: 'other', label: 'Other' },
  ];

  const totalShare = data.beneficiaries.reduce((sum, b) => sum + (parseFloat(b.share) || 0), 0);
  const isBalanced = Math.abs(totalShare - 100) < 0.01;

  const addBeneficiary = () => {
    const remaining = Math.max(0, 100 - totalShare);
    onChange('beneficiaries', [...data.beneficiaries, {
      id: Date.now(), name: '', relationship: '', share: remaining.toFixed(0), dob: '', notes: '',
    }]);
  };

  const updateB = (id, field, value) =>
    onChange('beneficiaries', data.beneficiaries.map(b => b.id === id ? { ...b, [field]: value } : b));

  const removeB = (id) => {
    if (data.beneficiaries.length <= 1) return;
    onChange('beneficiaries', data.beneficiaries.filter(b => b.id !== id));
  };

  // Detect forced heirship jurisdictions
  const hasForcedHeirship = nuance && (
    nuance.spouseRights.toLowerCase().includes('forced') ||
    nuance.spouseRights.toLowerCase().includes('réserve') ||
    nuance.spouseRights.toLowerCase().includes('legitim') ||
    nuance.spouseRights.toLowerCase().includes('pflichteil') ||
    nuance.spouseRights.toLowerCase().includes('ihou') ||
    nuance.spouseRights.toLowerCase().includes('laglott') ||
    nuance.spouseRights.toLowerCase().includes('quota') ||
    nuance.spouseRights.toLowerCase().includes('sharia')
  );

  return (
    <div className="space-y-6">
      <StepHeader icon={FiUsers} title="Beneficiaries"
        subtitle="Who should inherit your estate? Add everyone you wish to include and assign their share."
        step="Step 3 of 4" color="bg-purple-50 text-purple-500" />

      {/* Forced heirship warning */}
      {hasForcedHeirship && nuance && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-black text-amber-800 uppercase tracking-widest mb-1">
              Forced Heirship Applies — {nuance.country}
            </p>
            <p className="text-xs text-amber-700 font-light leading-relaxed">{nuance.spouseRights}</p>
            <p className="text-[10px] text-amber-600 font-light mt-1">Certain heirs have a legal right to a reserved share of your estate that cannot be overridden by a will. Consult a local attorney.</p>
          </div>
        </div>
      )}

      {/* Share Tracker */}
      <div className="p-5 rounded-2xl border-2 border-brand-dark bg-white">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Total Share Allocated</p>
          <span className={`text-lg font-black font-serif ${isBalanced ? 'text-green-600' : totalShare > 100 ? 'text-red-500' : 'text-accent-gold'}`}>
            {totalShare.toFixed(0)}% / 100%
          </span>
        </div>
        <div className="h-2.5 bg-brand-light rounded-full overflow-hidden">
          <motion.div animate={{ width: `${Math.min(totalShare, 100)}%` }}
            className={`h-full rounded-full transition-all duration-500 ${isBalanced ? 'bg-green-500' : totalShare > 100 ? 'bg-red-400' : 'bg-accent-gold'}`} />
        </div>
        {!isBalanced && (
          <p className={`text-[10px] mt-2 font-bold ${totalShare > 100 ? 'text-red-500' : 'text-accent-gold'}`}>
            {totalShare > 100
              ? `⚠ Over-allocated by ${(totalShare - 100).toFixed(0)}% — please adjust.`
              : `${(100 - totalShare).toFixed(0)}% remaining to allocate.`}
          </p>
        )}
        {isBalanced && (
          <p className="text-[10px] mt-2 font-bold text-green-600 flex items-center gap-1">
            <SafeIcon icon={FiCheckCircle} className="w-3 h-3" /> Perfectly balanced.
          </p>
        )}
      </div>

      {/* Beneficiary Cards */}
      <div className="space-y-4">
        <AnimatePresence>
          {data.beneficiaries.map((b, idx) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: -20 }}
              className="p-5 bg-white border-2 border-brand-dark rounded-2xl space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-accent-gold/10 text-accent-gold rounded-xl flex items-center justify-center text-xs font-black">{idx + 1}</div>
                  <p className="font-serif text-accent-navy">{b.name || `Beneficiary ${idx + 1}`}</p>
                </div>
                {data.beneficiaries.length > 1 && (
                  <button onClick={() => removeB(b.id)}
                    className="w-8 h-8 bg-red-50 text-red-400 hover:bg-red-100 rounded-xl flex items-center justify-center transition-all">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Full Legal Name" placeholder="e.g. Emma Smith" required value={b.name} onChange={v => updateB(b.id, 'name', v)} />
                <SelectField label="Relationship" required options={RELATIONSHIP} value={b.relationship} onChange={v => updateB(b.id, 'relationship', v)} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-1">
                    Share (%) <span className="text-accent-gold">*</span>
                  </label>
                  <div className="relative">
                    <input type="number" min="0" max="100" step="1" value={b.share}
                      onChange={e => updateB(b.id, 'share', e.target.value)}
                      className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-all pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted text-sm font-bold">%</span>
                  </div>
                </div>
                <Field label="Date of Birth (if person)" placeholder="Optional" type="date" value={b.dob} onChange={v => updateB(b.id, 'dob', v)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Special Notes</label>
                <input value={b.notes} onChange={e => updateB(b.id, 'notes', e.target.value)}
                  placeholder="e.g. Receives family home. If deceased, share passes to their children."
                  className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-xl focus:border-accent-gold outline-none text-sm transition-all placeholder:text-text-muted/50" />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <button onClick={addBeneficiary}
        className="w-full py-4 border-2 border-dashed border-brand-dark hover:border-accent-gold rounded-2xl flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-accent-gold transition-all">
        <SafeIcon icon={FiPlus} className="w-4 h-4" /> Add Another Beneficiary
      </button>

      <div className="p-5 bg-brand-light border-2 border-brand-dark rounded-2xl space-y-3">
        <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Residuary Estate</p>
        <p className="text-xs text-text-muted font-light">Who should receive any assets not specifically mentioned in your will?</p>
        <Field label="Residuary Beneficiary"
          placeholder="e.g. Jane Smith, or 'divided equally among all named beneficiaries'"
          value={data.residuary} onChange={v => onChange('residuary', v)} />
      </div>

      <InfoBox text="If a beneficiary predeceases you, their share will pass to the residuary estate unless you specify otherwise in the 'Special Notes' field." />
    </div>
  );
};

// ── Step 4: Review & Confirm ───────────────────────────────────────────────
const StepReview = ({ personal, executor, beneficiaries }) => {
  const nuance = useMemo(() => personal.country && personal.region
    ? getJurisdictionNuance(personal.country, personal.region) : null,
    [personal.country, personal.region]);

  const ReviewRow = ({ label, value }) => (
    <div className="flex items-start justify-between py-2.5 border-b border-brand-light last:border-0">
      <span className="text-[10px] font-black uppercase tracking-widest text-text-muted flex-shrink-0 w-36">{label}</span>
      <span className="text-sm font-bold text-accent-navy text-right">{value || '—'}</span>
    </div>
  );

  const Section = ({ icon, title, color, children }) => (
    <div className="bg-white border-2 border-brand-dark rounded-2xl overflow-hidden">
      <div className={`flex items-center gap-3 px-6 py-4 border-b border-brand-dark ${color}`}>
        <SafeIcon icon={icon} className="w-5 h-5" />
        <p className="font-serif text-lg text-accent-navy">{title}</p>
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  );

  const totalShare = beneficiaries.beneficiaries.reduce((s, b) => s + (parseFloat(b.share) || 0), 0);
  const countryObj = COUNTRIES.find(c => c.value === personal.country);

  return (
    <div className="space-y-6">
      <StepHeader icon={FiFileText} title="Review & Confirm"
        subtitle="Please review all information carefully before generating your will draft."
        step="Step 4 of 4" color="bg-green-50 text-green-500" />

      {/* Jurisdiction Summary */}
      {nuance && (
        <div className="p-5 bg-accent-navy text-white rounded-2xl">
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiGlobe} className="w-5 h-5 text-accent-gold" />
            <p className="font-serif text-lg">Jurisdiction: {nuance.country} — {personal.region}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { label: 'Legal System', value: nuance.legalSystem },
              { label: 'Witnesses Needed', value: nuance.witnesses === 0 ? 'None (holographic)' : nuance.witnesses },
              { label: 'Notarisation', value: nuance.notarisation },
              { label: 'Holographic', value: nuance.holographic },
              { label: 'Probate', value: nuance.probate },
              { label: 'Executor Title', value: nuance.executorTerm },
            ].map(r => (
              <div key={r.label}>
                <p className="text-[9px] font-black uppercase tracking-widest text-white/50 mb-0.5">{r.label}</p>
                <p className="text-xs font-bold text-white">{r.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <Section icon={FiUser} title="Personal Information" color="bg-blue-50/50">
        <ReviewRow label="Full Name" value={`${personal.firstName} ${personal.middleName} ${personal.lastName}`.trim()} />
        <ReviewRow label="Date of Birth" value={personal.dob} />
        <ReviewRow label="Marital Status" value={personal.maritalStatus} />
        <ReviewRow label="Country" value={countryObj?.label} />
        <ReviewRow label="Region" value={personal.region} />
        <ReviewRow label="Address" value={personal.address} />
        {personal.nationality && <ReviewRow label="Nationality" value={personal.nationality} />}
        {personal.spouseName && <ReviewRow label="Spouse / Partner" value={personal.spouseName} />}
      </Section>

      <Section icon={FiShield} title={`${nuance?.executorTerm || 'Executor'}`} color="bg-accent-gold/5">
        <ReviewRow label="Primary" value={executor.primaryName} />
        <ReviewRow label="Relationship" value={executor.primaryRelationship} />
        <ReviewRow label="Contact" value={executor.primaryEmail || executor.primaryPhone} />
        {executor.alternateName && <ReviewRow label="Alternate" value={executor.alternateName} />}
        <ReviewRow label="Powers" value={[
          executor.sellProperty && 'Sell Property',
          executor.manageInvestments && 'Manage Investments',
          executor.payDebts && 'Pay Debts & Taxes',
          executor.digitalAccess && 'Digital Access',
        ].filter(Boolean).join(', ') || 'None'} />
      </Section>

      <Section icon={FiUsers} title="Beneficiaries" color="bg-purple-50/50">
        {beneficiaries.beneficiaries.map((b, i) => (
          <ReviewRow key={b.id} label={`Beneficiary ${i + 1}`}
            value={`${b.name} (${b.relationship}) — ${b.share}%`} />
        ))}
        <ReviewRow label="Total Share" value={`${totalShare.toFixed(0)}%`} />
        {beneficiaries.residuary && <ReviewRow label="Residuary" value={beneficiaries.residuary} />}
      </Section>

      {/* Jurisdiction-specific disclaimer */}
      <div className="p-5 bg-amber-50 border-2 border-amber-100 rounded-2xl flex items-start gap-4">
        <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-black text-amber-800 uppercase tracking-widest mb-1">Legal Disclaimer</p>
          <p className="text-xs text-amber-700 leading-relaxed font-light">
            This AI-generated document is a <strong>draft template only</strong> and does not constitute legal advice.
            {nuance && ` In ${nuance.country}, this document requires ${nuance.witnesses > 0 ? `${nuance.witnesses} witness${nuance.witnesses > 1 ? 'es' : ''}` : 'no witnesses for a holographic form'} and ${nuance.notarisation.toLowerCase()}.`}
            {' '}It must be reviewed by a licensed attorney in your jurisdiction before it is legally valid.
          </p>
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <div className="w-5 h-5 border-2 border-accent-gold rounded flex items-center justify-center flex-shrink-0 mt-0.5 bg-accent-gold">
          <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-white" />
        </div>
        <p className="text-xs text-text-muted font-light leading-relaxed">
          I understand this is a draft template for review purposes only and agree to consult a licensed attorney before finalising any legal document.
        </p>
      </label>
    </div>
  );
};

// ── Shared Sub-components ──────────────────────────────────────────────────
const StepHeader = ({ icon, title, subtitle, step, color }) => (
  <div className="flex items-start gap-4 pb-2">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <SafeIcon icon={icon} className="w-6 h-6" />
    </div>
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">{step}</p>
      <h3 className="text-xl font-serif text-accent-navy leading-tight">{title}</h3>
      <p className="text-sm text-text-muted font-light mt-1 leading-relaxed">{subtitle}</p>
    </div>
  </div>
);

const InfoBox = ({ text }) => (
  <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
    <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
    <p className="text-[11px] text-blue-700 font-light leading-relaxed">{text}</p>
  </div>
);

// ── Progress Indicator ─────────────────────────────────────────────────────
const ProgressBar = ({ step }) => {
  const steps = [
    { label: 'Personal', icon: FiUser },
    { label: 'Executor', icon: FiShield },
    { label: 'Beneficiaries', icon: FiUsers },
    { label: 'Review', icon: FiFileText },
  ];
  return (
    <div className="px-6 md:px-10 py-5 border-b border-brand-dark bg-brand-light">
      <div className="flex items-center">
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center border-2 transition-all ${
                i < step ? 'bg-accent-gold border-accent-gold text-white'
                : i === step ? 'bg-accent-navy border-accent-navy text-white'
                : 'bg-white border-brand-dark text-text-muted'
              }`}>
                {i < step
                  ? <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                  : <SafeIcon icon={s.icon} className="w-4 h-4" />}
              </div>
              <span className={`text-[8px] font-black uppercase tracking-widest hidden sm:block ${i <= step ? 'text-accent-navy' : 'text-text-muted'}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 sm:mx-2 mb-4 transition-all ${i < step ? 'bg-accent-gold' : 'bg-brand-dark'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// ── Generating View ────────────────────────────────────────────────────────
const GeneratingView = ({ personal }) => {
  const nuance = personal.country && personal.region
    ? getJurisdictionNuance(personal.country, personal.region) : null;

  const tasks = [
    'Validating personal information…',
    `Applying ${nuance ? nuance.country : 'jurisdiction'}-specific legal language…`,
    `Structuring ${nuance ? nuance.executorTerm.toLowerCase() : 'executor'} provisions…`,
    'Drafting beneficiary clauses…',
    nuance?.witnesses > 0 ? `Inserting ${nuance.witnesses}-witness attestation clause…` : 'Formatting holographic will structure…',
    'Generating residuary estate section…',
    'Finalising document structure…',
  ];

  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
      <div className="relative w-24 h-24 mb-8">
        <div className="absolute inset-0 rounded-full border-4 border-brand-dark border-t-accent-gold animate-spin" />
        <div className="absolute inset-3 rounded-full border-4 border-brand-dark border-b-accent-navy animate-spin"
          style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
        <div className="absolute inset-0 flex items-center justify-center">
          <SafeIcon icon={FiCpu} className="w-7 h-7 text-accent-navy" />
        </div>
      </div>
      <h3 className="text-2xl font-serif text-accent-navy mb-2">Drafting Your Will…</h3>
      {nuance && (
        <p className="text-xs font-bold text-accent-gold mb-1">{nuance.country} · {nuance.legalSystem}</p>
      )}
      <p className="text-sm text-text-muted font-light mb-8 max-w-xs">Generating a legally-structured draft tailored to your jurisdiction.</p>
      <div className="space-y-3 w-full max-w-sm text-left">
        {tasks.map((t, i) => (
          <motion.div key={t} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.4 }}
            className="flex items-center gap-3 text-xs text-text-muted">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.4 + 0.2 }}
              className="w-4 h-4 bg-accent-gold/20 text-accent-gold rounded-full flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiCheckCircle} className="w-2.5 h-2.5" />
            </motion.div>
            {t}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ── Done View ─────────────────────────────────────────────────────────────
const DoneView = ({ onStartOver, personal }) => {
  const nuance = personal.country && personal.region
    ? getJurisdictionNuance(personal.country, personal.region) : null;

  return (
    <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
        className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-8 shadow-xl shadow-green-200">
        <SafeIcon icon={FiCheckCircle} className="w-12 h-12" />
      </motion.div>
      <h3 className="text-3xl font-serif text-accent-navy mb-3">Your Draft Is Ready</h3>
      {nuance && (
        <div className="inline-flex items-center gap-2 bg-accent-navy/5 border border-accent-navy/10 px-4 py-2 rounded-full mb-4">
          <SafeIcon icon={FiGlobe} className="w-3.5 h-3.5 text-accent-navy" />
          <span className="text-xs font-bold text-accent-navy">{nuance.country} · {nuance.legalSystem}</span>
        </div>
      )}
      <p className="text-sm text-text-muted font-light max-w-sm mx-auto mb-8 leading-relaxed">
        Your AI-generated will draft has been prepared. <strong className="text-accent-navy">Please review it with a licensed attorney</strong> before signing or filing.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm">
        <button className="btn-primary py-4 flex items-center justify-center gap-2 flex-1">
          <SafeIcon icon={FiDownload} /> Download Draft (PDF)
        </button>
        <button onClick={onStartOver} className="btn-secondary py-4 flex items-center justify-center gap-2 flex-1">
          <SafeIcon icon={FiEdit3} /> Start Over
        </button>
      </div>
      {nuance && (
        <div className="mt-8 p-5 bg-amber-50 border border-amber-100 rounded-2xl text-left max-w-sm space-y-2">
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-700">Next Steps — {nuance.country}</p>
          <ul className="space-y-1.5">
            {[
              nuance.witnesses > 0 && `Have ${nuance.witnesses} independent witness${nuance.witnesses > 1 ? 'es' : ''} sign the document`,
              nuance.notarisation !== 'Not required' && `Notarisation: ${nuance.notarisation}`,
              `Probate process: ${nuance.probate}`,
              'Consult a licensed estate attorney or notary in your jurisdiction',
            ].filter(Boolean).map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px] text-amber-700 font-light">
                <span className="w-4 h-4 bg-amber-200 text-amber-700 rounded-full flex items-center justify-center text-[9px] font-black flex-shrink-0 mt-0.5">{i + 1}</span>
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Initial State ──────────────────────────────────────────────────────────
const INIT_PERSONAL = {
  country: '', region: '', firstName: '', middleName: '', lastName: '',
  dob: '', maritalStatus: '', nationality: '', phone: '', email: '',
  address: '', spouseName: '', spouseDob: '',
};

const INIT_EXECUTOR = {
  primaryName: '', primaryRelationship: '', primaryPhone: '', primaryEmail: '', primaryAddress: '',
  alternateName: '', alternateRelationship: '', alternatePhone: '', alternateEmail: '',
  sellProperty: true, manageInvestments: true, payDebts: true, digitalAccess: false,
};

const INIT_BENEFICIARIES = {
  beneficiaries: [{ id: 1, name: '', relationship: '', share: '100', dob: '', notes: '' }],
  residuary: '',
};

// ── Main Modal ─────────────────────────────────────────────────────────────
const WillDraftingModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const [personal, setPersonal] = useState(INIT_PERSONAL);
  const [executor, setExecutor] = useState(INIT_EXECUTOR);
  const [beneficiaries, setBeneficiaries] = useState(INIT_BENEFICIARIES);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDone, setIsDone] = useState(false);

  const updatePersonal = (k, v) => setPersonal(p => ({ ...p, [k]: v }));
  const updateExecutor = (k, v) => setExecutor(p => ({ ...p, [k]: v }));
  const updateBeneficiaries = (k, v) => setBeneficiaries(p => ({ ...p, [k]: v }));

  const canProceed = () => {
    if (step === 0) return personal.country && personal.region && personal.firstName && personal.lastName && personal.address;
    if (step === 1) return executor.primaryName && executor.primaryRelationship;
    if (step === 2) {
      const total = beneficiaries.beneficiaries.reduce((s, b) => s + (parseFloat(b.share) || 0), 0);
      return beneficiaries.beneficiaries.every(b => b.name && b.relationship) && Math.abs(total - 100) < 0.01;
    }
    return true;
  };

  const handleNext = () => {
    if (step < 3) { setStep(s => s + 1); return; }
    setIsGenerating(true);
    setTimeout(() => { setIsGenerating(false); setIsDone(true); }, 3800);
  };

  const handleStartOver = () => {
    setStep(0); setPersonal(INIT_PERSONAL); setExecutor(INIT_EXECUTOR);
    setBeneficiaries(INIT_BENEFICIARIES); setIsGenerating(false); setIsDone(false);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={e => e.target === e.currentTarget && onClose()}>
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden flex flex-col"
          style={{ maxHeight: '90vh' }}>

          {/* Header */}
          <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] px-6 md:px-10 py-5 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-accent-gold rounded-2xl flex items-center justify-center shadow-lg shadow-accent-gold/30">
                <SafeIcon icon={FiCpu} className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">AI-Powered · Global · Beta</p>
                <h2 className="text-lg font-serif text-white">Will Drafting Interview</h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {personal.country && personal.region && (
                <div className="hidden sm:flex items-center gap-1.5 bg-white/10 border border-white/10 px-3 py-1.5 rounded-xl">
                  <SafeIcon icon={FiMapPin} className="w-3 h-3 text-accent-gold" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-white/70">
                    {COUNTRIES.find(c => c.value === personal.country)?.label.split(' ').slice(1).join(' ')} · {personal.region}
                  </span>
                </div>
              )}
              <div className="hidden sm:flex items-center gap-1.5 bg-white/10 border border-white/10 px-3 py-1.5 rounded-xl">
                <SafeIcon icon={FiLock} className="w-3 h-3 text-accent-gold" />
                <span className="text-[9px] font-black uppercase tracking-widest text-white/70">Encrypted</span>
              </div>
              <button onClick={onClose}
                className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all">
                <SafeIcon icon={FiX} className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {!isGenerating && !isDone && <ProgressBar step={step} />}

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 md:p-10">
            <AnimatePresence mode="wait">
              {isDone ? (
                <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <DoneView onStartOver={handleStartOver} personal={personal} />
                </motion.div>
              ) : isGenerating ? (
                <motion.div key="generating" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <GeneratingView personal={personal} />
                </motion.div>
              ) : (
                <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.22 }}>
                  {step === 0 && <StepPersonal data={personal} onChange={updatePersonal} />}
                  {step === 1 && <StepExecutor data={executor} onChange={updateExecutor} personal={personal} />}
                  {step === 2 && <StepBeneficiaries data={beneficiaries} onChange={updateBeneficiaries} personal={personal} />}
                  {step === 3 && <StepReview personal={personal} executor={executor} beneficiaries={beneficiaries} />}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          {!isGenerating && !isDone && (
            <div className="flex-shrink-0 px-6 md:px-10 py-5 border-t border-brand-dark bg-brand-light flex items-center justify-between gap-4">
              <span className="text-[9px] font-black uppercase tracking-widest text-text-muted hidden sm:block">
                {step + 1} of 4 steps
              </span>
              <div className="flex items-center gap-3 ml-auto">
                {step > 0 && (
                  <button onClick={() => setStep(s => s - 1)}
                    className="flex items-center gap-2 px-6 py-3 border-2 border-brand-dark rounded-xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-navy hover:text-accent-navy transition-all">
                    <SafeIcon icon={FiArrowLeft} className="w-4 h-4" /> Back
                  </button>
                )}
                <button onClick={handleNext} disabled={!canProceed()}
                  className={`flex items-center gap-2 px-8 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    canProceed()
                      ? 'bg-accent-navy text-white hover:bg-accent-navy/90 shadow-lg shadow-accent-navy/20'
                      : 'bg-brand-dark text-text-muted cursor-not-allowed opacity-60'
                  }`}>
                  {step === 3 ? 'Generate Will Draft' : 'Continue'}
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WillDraftingModal;